/**
 * The rendering gate for a `MockEnvironment`: is the world actually drawing?
 *
 * A game's state assertions once passed on a black world — every system ran, every value was right, and the
 * canvas showed nothing. `env.expectRendering()` answers the rendering question on its own, from the canvas's
 * PIXELS: read through a small 2d probe (`readCanvasPixels`), judged by the one luma rule (`blackFraction`),
 * and read inside the client's tick — after its systems' `onUpdate` drew, before the game file's `onAfterUpdate`
 * and before the frame is presented — so a WebGL frame drawn in a system needs no `preserveDrawingBuffer`.
 * It fails in distinct ways, each named for what it saw: a canvas that is BLACK, one that reads fully TRANSPARENT,
 * one it could not READ at all, one whose WebGL context was LOST — known from the canvas's own
 * `webglcontextlost` event, which test-utils records for every client's canvas from its creation — and a client whose
 * canvas cannot be JUDGED (its loop stopped, or its every tick aborts). One cause can look like another: a loss
 * test-utils did not witness reads as fully transparent.
 */
import type { ProcessResolver, ProcessTarget, PumpableProcess } from './environment-lookup.js';
import type { TickQueries } from './environment-ticks.js';
import { DEFAULT_RENDER_BLACK_LIMIT, LUMA_BLACK_MAX, blackFraction, isContextLost, readCanvasPixels } from './canvas-assertions.js';

/** Thrown by `expectRendering` when a canvas's WebGL context was LOST — distinct from "did not render" and from "unreadable" (one cause: Chromium keeps at most 16 WebGL contexts live and evicts the oldest beyond that). */
export class RenderingContextLostError extends Error {
  override readonly name = 'RenderingContextLostError';
}

/** What `env.expectRendering` judges, and how it samples. */
export interface ExpectRenderingOptions {
  /** Which client's canvas (its position in the client list, as `{ client }` targets count it); default 0. `'all'` judges every client's own canvas, on the same ticks. */
  client?: number | 'all';
  /** Judge this canvas instead of the client's `envOptions.canvas`. With a client index it is still read inside that client's tick (after its systems' `onUpdate`); alone it is sampled on the wall clock, between frames, where only `preserveDrawingBuffer` guarantees a WebGL frame is still there. */
  canvas?: HTMLCanvasElement;
  /** Ceiling (0 < limit ≤ 1) on the pure-black fraction of the BEST sample. Default `DEFAULT_RENDER_BLACK_LIMIT`. */
  blackLimit?: number;
  /** How many samples to take (default 6); a sparse present is missed by a single readback. The first one under `blackLimit` passes. */
  samples?: number;
  /** Gap between samples (default 400): GAME milliseconds on the client's own ticks; wall milliseconds for a canvas sampled alone. */
  gapMs?: number;
  /** A fractional sub-rect (0..1) of the canvas — one half of a split viewport. */
  region?: { x: number; y: number; w: number; h: number };
  /** A pixel at or below this Rec. 709 luma counts as black. Default `LUMA_BLACK_MAX`. */
  lumaBlack?: number;
  /** Wall guard for a client whose loop stopped: the gate fails as "cannot be judged" once the client has not ticked for this long (default 60 000). */
  hangGuardMs?: number;
  /**
   * What the gate judges, for its failure — the moment or view it checks ('the floor after the transition'). It leads
   * every failure's text, ahead of the gate's own evidence: `[test-utils] expectRendering: <message> — <evidence>`. The
   * contract of `env.waitFor`'s `message`: a FUNCTION is evaluated only when the gate fails (never on a pass), so it can
   * embed live state. The failure keeps the gate's own error as its `cause`, and its type (`RenderingContextLostError`
   * stays one); a function that throws is named in the text in its place, and the evidence still follows.
   */
  message?: string | (() => string);
}

/** What a passing `expectRendering` saw on one canvas. */
export interface RenderingReport {
  /** The canvas judged. */
  canvas: HTMLCanvasElement;
  /** The pure-black fraction of the best sample — the one that passed. */
  bestBlackFraction: number;
  /** How many samples were taken, the passing one included. */
  samples: number;
}

/** The rendering gate every `MockEnvironment` answers, mixed into it by `initializer`. */
export interface RenderQueries {
  /**
   * Assert a canvas is presenting a rendered world: the best of `samples` readbacks is under `blackLimit`.
   * A client's canvas is read inside that client's tick — after its systems' `onUpdate`, before the game file's
   * `onAfterUpdate` and before the frame is presented — so a WebGL frame drawn in a system's `onUpdate` needs no
   * `preserveDrawingBuffer`; one drawn in `onAfterUpdate` is not there yet when the gate reads, so that canvas
   * needs `preserveDrawingBuffer: true` (the gate then reads the previous frame). A lost context throws
   * `RenderingContextLostError` — recorded from the canvas's `webglcontextlost` event (a client's canvas is watched
   * from its creation, a canvas you name from the gate's start), or asked of a canvas that showed paint; a canvas
   * that reads fully transparent fails as such and is never asked for a context; an unreadable canvas throws naming
   * that, never "black"; a client that stops ticking fails on `hangGuardMs` as "cannot be judged", and so does one
   * whose ticks keep aborting before their after phase (a system throwing on every update), named as such, once
   * at least max(`samples`, 3) of them — one bad frame is not every update — and `samples × gapMs` of its game time
   * aborted in a row. `{ client: 'all' }` resolves one report per client.
   */
  expectRendering(options: ExpectRenderingOptions & { client: 'all' }): Promise<RenderingReport[]>;
  expectRendering(options?: ExpectRenderingOptions & { client?: number }): Promise<RenderingReport>;
  expectRendering(options?: ExpectRenderingOptions): Promise<RenderingReport | RenderingReport[]>;
}

/** A client's process carries its client, and the client the canvas it was created with. */
type ClientProcess = PumpableProcess & { client?: { envOptions?: { canvas?: HTMLCanvasElement } } };

/** The context-loss record: which canvases are lost, and which are watched. */
interface ContextLossRecord {
  /** Canvases whose WebGL context is lost, as their own events said: `webglcontextlost` adds one, `webglcontextrestored` removes it. */
  readonly lost: WeakSet<HTMLCanvasElement>;
  /** Canvases whose events are listened to already. */
  readonly watched: WeakSet<HTMLCanvasElement>;
}

const CONTEXT_LOSS_KEY = Symbol.for('gotti.test-utils.context-loss');

/**
 * The record lives on `globalThis` under ONE `Symbol.for` key — the mechanism `focus-scoped-input-state.ts` uses for
 * the input router's state — so every copy of this module in a page (the playground's pre-bundled one and the game's
 * own, a module HMR re-evaluated) reads and writes the same record. With module-local sets, a loss recorded through
 * one copy's `watchContextLoss` was invisible to another copy's gate. Its own key, not the router's: the two records
 * have nothing to do with each other.
 */
const contextLossRecord = (): ContextLossRecord => {
  const holder = globalThis as unknown as Record<symbol, ContextLossRecord | undefined>;
  return (holder[CONTEXT_LOSS_KEY] ??= { lost: new WeakSet(), watched: new WeakSet() });
};

/**
 * Records `canvas`'s WebGL context loss from its own `webglcontextlost` event (cleared by `webglcontextrestored`), so
 * `expectRendering` reports a lost context as lost without asking the canvas for a context — asking a canvas that has
 * none would create one — and whatever the lost canvas reads back: the last frame read from it while live, else fully
 * transparent. Chromium fires the event a task after the loss. `initializer` watches every client's canvas when the
 * client is created; the gate watches a canvas it is handed when it starts — a loss before that goes unrecorded.
 * The record is one per page, shared by every copy of test-utils in it. Listening creates no context. Idempotent; a
 * value that is not an element is ignored.
 */
export const watchContextLoss = (canvas: HTMLCanvasElement | null | undefined): void => {
  const record = contextLossRecord();
  if (!canvas || typeof canvas.addEventListener !== 'function' || record.watched.has(canvas)) return;
  record.watched.add(canvas);
  canvas.addEventListener('webglcontextlost', () => { record.lost.add(canvas); });
  canvas.addEventListener('webglcontextrestored', () => { record.lost.delete(canvas); });
};

const PREFIX = '[test-utils] expectRendering';
/**
 * Float slack on a gap: a 30 Hz client ticks 1/30 s, and twelve of those deltas sum to 399.99999999999994 ms — a
 * rounding error short of the default 400 ms gap, which must still count as reached.
 */
const GAP_SLACK_MS = 1e-6;
/**
 * The shortest run of aborted ticks the gate blames on a system throwing on every update, however small `samples`
 * is: one bad frame — or two — is not every update. The run must also cover `samples × gapMs` of the client's game.
 */
const MIN_ABORTED_RUN = 3;
/** True when any pixel has alpha > 0 — something was painted. */
const hasPaint = (pixels: ImageData): boolean => {
  for (let i = 3; i < pixels.data.length; i += 4) if (pixels.data[i] > 0) return true;
  return false;
};
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const percent = (fraction: number) => `${(fraction * 100).toFixed(1)}%`;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * The gate's failure `err`, led by the caller's `message`: `<PREFIX>: <message> — <evidence>`, where the evidence is
 * `err`'s own text without the prefix. A function message is evaluated here, once the gate has failed; one that throws
 * is named in its place, so the evidence is never lost to it. The new error keeps `err`'s type for a lost context —
 * the GPU, not the game, is still the story — and `err` itself as its `cause`.
 */
const ledBy = (message: string | (() => string), err: unknown): Error => {
  let lead: string;
  try {
    lead = typeof message === 'function' ? String(message()) : message;
  } catch (thrown) {
    lead = `(its message threw: ${thrown instanceof Error ? thrown.message : String(thrown)})`;
  }
  const text = err instanceof Error ? err.message : String(err);
  const evidence = text.startsWith(`${PREFIX}: `) ? text.slice(PREFIX.length + 2) : text;
  const Failure = err instanceof RenderingContextLostError ? RenderingContextLostError : Error;
  return new Failure(`${PREFIX}: ${lead} — ${evidence}`, { cause: err });
};

/** The options a judgement runs on, every default applied and every value checked. */
type Judgement = Required<Omit<ExpectRenderingOptions, 'client' | 'canvas' | 'region' | 'message'>> & { region: ExpectRenderingOptions['region'] | null };
/** Why sampling inside a client's ticks ended with no verdict: it stopped ticking, or its ticks kept aborting. */
type Unjudged = { idleMs: number } | { abortedTicks: number; abortedMs: number };

const settle = (options: ExpectRenderingOptions): Judgement => {
  const {
    blackLimit = DEFAULT_RENDER_BLACK_LIMIT, samples = 6, gapMs = 400, region = null, lumaBlack = LUMA_BLACK_MAX, hangGuardMs = 60_000,
  } = options;
  const refuse = (what: string) => { throw new Error(`${PREFIX}: ${what}`); };
  if (!Number.isInteger(samples) || samples < 1) refuse(`samples must be a whole number ≥ 1 (got ${samples})`);
  if (!(blackLimit > 0 && blackLimit <= 1)) refuse(`blackLimit must be a fraction in (0, 1] (got ${blackLimit})`);
  if (!Number.isFinite(gapMs) || gapMs < 0) refuse(`gapMs must be a finite number of milliseconds ≥ 0 (got ${gapMs})`);
  if (!Number.isFinite(hangGuardMs) || hangGuardMs <= 0) refuse(`hangGuardMs must be a finite number of milliseconds > 0 (got ${hangGuardMs})`);
  if (!(lumaBlack >= 0 && lumaBlack <= 255)) refuse(`lumaBlack must be a luma in [0, 255] (got ${lumaBlack})`);
  if (region && !(region.x >= 0 && region.y >= 0 && region.w > 0 && region.h > 0 && region.x + region.w <= 1 && region.y + region.h <= 1)) {
    refuse(`region must lie inside the canvas — 0 ≤ x, 0 ≤ y, 0 < w, 0 < h, x + w ≤ 1, y + h ≤ 1 (got ${JSON.stringify(region)})`);
  }
  if (options.client === 'all' && options.canvas) {
    refuse("{ client: 'all' } judges each client's own canvas — pass { canvas } with one client index instead.");
  }
  return { blackLimit, samples, gapMs, region, lumaBlack, hangGuardMs };
};

/**
 * Builds the rendering gate for one environment: clients are resolved through its one `resolver`, and a client's
 * canvas is sampled inside its ticks through `ticks.onTick`. `initializer` attaches the result to the env after
 * the tick mixin.
 */
export const createRenderQueries = (resolver: ProcessResolver, ticks: Pick<TickQueries, 'onTick'>): RenderQueries => {
  /** Judge one canvas: sample it (inside `client`'s ticks when given, else on the wall clock) and give the verdict. */
  const judge = async (canvas: HTMLCanvasElement, client: number | null, o: Judgement, label: string): Promise<RenderingReport> => {
    // A client's canvas is watched from its creation already; a canvas the caller names, from now on.
    watchContextLoss(canvas);
    let best: number | null = null;
    let unreadable = 0;
    let taken = 0;
    // Did any sample show a pixel with alpha > 0? Only a canvas that holds a context can.
    let painted = false;
    /** One readback; `true` when it passes. */
    const take = (): boolean => {
      taken += 1;
      const pixels = readCanvasPixels(canvas, o.region);
      if (!pixels) {
        unreadable += 1;
        return false;
      }
      painted ||= hasPaint(pixels);
      const fraction = blackFraction(pixels, o.lumaBlack);
      best = best === null ? fraction : Math.min(best, fraction);
      return fraction < o.blackLimit;
    };

    if (client === null) {
      for (let i = 0; i < o.samples; i++) {
        if (i > 0) await sleep(o.gapMs);
        if (take()) break;
      }
    } else {
      const unjudged = await sampleInTicks(client, o, take);
      const takenNote = `(${taken} of ${plural(o.samples, 'sample')} taken)`;
      if (unjudged && 'idleMs' in unjudged) {
        throw new Error(`${PREFIX}: client ${client} did not tick for ${unjudged.idleMs}ms — its loop is stopped or starved, so ${label} cannot be judged ${takenNote}.`);
      }
      if (unjudged) {
        throw new Error(
          `${PREFIX}: client ${client}'s ticks are aborting — ${unjudged.abortedTicks} in a row (${Math.round(unjudged.abortedMs)}ms of its game) ended before their after phase: ` +
            `a system may be throwing on every update (the game loops log each throw), so ${label} cannot be judged ${takenNote}.`,
        );
      }
    }

    const verdict: number | null = best;
    const unreadableNote = unreadable ? `; ${unreadable} unreadable` : '';
    const lost = () => new RenderingContextLostError(`${PREFIX}: ${label} has LOST its WebGL context (one cause: Chromium keeps at most 16 WebGL contexts live and evicts the oldest beyond that).`);
    // Lost is judged first, and first of all from the canvas's own event: a lost canvas reads back the last frame
    // read from it while live (measured in Chromium) — its pixels can pass — else fully transparent, which no other
    // check could tell from a canvas nothing has drawn on.
    if (contextLossRecord().lost.has(canvas)) throw lost();
    if (verdict === null) {
      throw new Error(`${PREFIX}: ${label} was unreadable on every one of ${plural(taken, 'sample')} (zero-size, detached from the document, or not drawable) — no pixel was read, so nothing was judged.`);
    }
    // The context is asked about only once a sample showed paint: a canvas showing paint holds a context, so asking
    // creates nothing, while asking a canvas that has none would GIVE it one (a 2d one, for good) — and a WebGL game
    // that initialises lazily could then never claim it.
    if (!painted) {
      throw new Error(
        `${PREFIX}: ${label} read fully transparent on every one of ${plural(taken - unreadable, 'sample')}${unreadableNote}. Three things look like that: ` +
        "no renderer has drawn on it yet; the game draws outside its systems' onUpdate (in onAfterUpdate, say) on a canvas made without preserveDrawingBuffer; " +
        'or its WebGL context was lost before anything read it back, while test-utils was not watching. Its context was not queried: asking a canvas that has none would create one.',
      );
    }
    // A loss no event recorded (it happened before anything watched): the canvas showed paint, so it holds a context.
    if (isContextLost(canvas)) throw lost();
    if (verdict < o.blackLimit) return { canvas, bestBlackFraction: verdict, samples: taken };
    throw new Error(`${PREFIX}: ${label} is ${percent(verdict)} black on the best of ${plural(taken, 'sample')} (limit ${percent(o.blackLimit)}${unreadableNote}) — the world is not rendering.`);
  };

  /**
   * Samples inside client `index`'s ticks, in the `after` phase (its systems drew; the frame is not presented yet):
   * on the first tick, then once `gapMs` of its game time passed, until `take` passes or `samples` were taken. The
   * game time is counted in the `before` phase, which every tick reaches — a tick a system aborted has no `after`.
   * Resolves `null` when done; `{ idleMs }` when the client stopped ticking for `hangGuardMs`; `{ abortedTicks,
   * abortedMs }` when its ticks kept aborting — none reached its after phase for at least max(`samples`,
   * `MIN_ABORTED_RUN`) ticks and `samples × gapMs` of its game time in a row, as long as the whole judgement could
   * take and never on one bad frame — so nothing could be read. A tick is known to have aborted when the next one begins.
   */
  const sampleInTicks = (index: number, o: Judgement, take: () => boolean) => new Promise<Unjudged | null>((resolve, reject) => {
    const target: ProcessTarget = { client: index };
    let taken = 0;
    let sinceSampleMs = 0;
    let lastTickAt = Date.now();
    /** The tick running now — its delta, and whether its after phase ran — and the aborted ticks just before it. */
    let current: { deltaMs: number; finished: boolean } | null = null;
    let abortedTicks = 0;
    let abortedMs = 0;
    let guard: ReturnType<typeof setTimeout> | undefined;
    // Settles exactly once: it stops the subscription (no listener call follows) and clears the guard (no watch follows).
    const finish = (outcome: () => void) => {
      stop();
      clearTimeout(guard);
      outcome();
    };
    const stop = ticks.onTick(target, (deltaMs, phase) => {
      lastTickAt = Date.now();
      if (phase === 'before') {
        if (current && !current.finished) {
          // The tick before this one ended without its after phase: a system's throw aborted it.
          abortedTicks += 1;
          abortedMs += current.deltaMs;
          if (abortedTicks >= Math.max(o.samples, MIN_ABORTED_RUN) && abortedMs >= o.samples * o.gapMs - GAP_SLACK_MS) {
            finish(() => resolve({ abortedTicks, abortedMs }));
            return;
          }
        }
        current = { deltaMs, finished: false };
        if (taken > 0) sinceSampleMs += deltaMs;
        return;
      }
      if (current) current.finished = true;
      abortedTicks = 0;
      abortedMs = 0;
      if (taken > 0 && sinceSampleMs < o.gapMs - GAP_SLACK_MS) return;
      sinceSampleMs = 0;
      taken += 1;
      // The framework swallows a throw inside a tick listener: catch it here, or the gate would wait forever.
      try {
        if (take() || taken >= o.samples) finish(() => resolve(null));
      } catch (err) {
        finish(() => reject(err));
      }
    });
    const watch = () => {
      const idleMs = Date.now() - lastTickAt;
      if (idleMs >= o.hangGuardMs) finish(() => resolve({ idleMs }));
      else guard = setTimeout(watch, o.hangGuardMs - idleMs);
    };
    guard = setTimeout(watch, o.hangGuardMs);
  });

  /** Client `index`'s canvas — `envOptions.canvas`, or `canvas` when the caller names one. */
  const clientCanvas = (index: number, canvas: HTMLCanvasElement | undefined): HTMLCanvasElement => {
    const count = resolver.clientProcesses().length;
    if (!Number.isInteger(index) || index < 0 || index >= count) throw new Error(`${PREFIX}: no client ${index} (${count} client(s)).`);
    const own = (resolver.clientProcess(index) as ClientProcess).client?.envOptions?.canvas;
    const judged = canvas ?? own;
    if (!judged) throw new Error(`${PREFIX}: client ${index} has no canvas (envOptions.canvas).`);
    return judged;
  };

  async function expectRendering(options: ExpectRenderingOptions = {}): Promise<RenderingReport | RenderingReport[]> {
    if (options.message === undefined) return judgeAll(options);
    try {
      return await judgeAll(options);
    } catch (err) {
      throw ledBy(options.message, err);
    }
  }

  /** Every judgement the options ask for; what it throws is the gate's own evidence. */
  async function judgeAll(options: ExpectRenderingOptions): Promise<RenderingReport | RenderingReport[]> {
    const o = settle(options);
    if (options.client !== 'all') {
      if (options.canvas && options.client === undefined) return judge(options.canvas, null, o, 'the canvas');
      const index = options.client ?? 0;
      return judge(clientCanvas(index, options.canvas), index, o, options.canvas ? 'the canvas' : `client ${index}'s canvas`);
    }
    const count = resolver.clientProcesses().length;
    if (count === 0) throw new Error(`${PREFIX}: { client: 'all' } names no client — this environment has none.`);
    // Every canvas is resolved before any sampling starts, so a client without one leaves nothing running behind it.
    const canvases = Array.from({ length: count }, (_, index) => clientCanvas(index, undefined));
    // Every client is judged over the same ticks: each one's sampling subscribes now, before any of them settles.
    const outcomes = await Promise.allSettled(canvases.map((canvas, index) => judge(canvas, index, o, `client ${index}'s canvas`)));
    const failures = outcomes.flatMap((outcome) => (outcome.status === 'rejected' ? [outcome.reason as Error] : []));
    if (failures.length === 1) throw failures[0];
    if (failures.length > 1) {
      const message = [`${PREFIX}: ${failures.length} of ${count} clients are not rendering:`, ...failures.map((err) => `  • ${err.message}`)].join('\n');
      // A lost context means the GPU, not the game, cannot render here: the error keeps that type.
      throw failures.some((err) => err instanceof RenderingContextLostError) ? new RenderingContextLostError(message) : new Error(message);
    }
    return outcomes.map((outcome) => (outcome as PromiseFulfilledResult<RenderingReport>).value);
  }

  // One implementation serves the three overloads: it returns an array exactly when `client` is 'all'.
  return { expectRendering: expectRendering as RenderQueries['expectRendering'] };
};
